import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import PageSeo from "@/components/feature/PageSeo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles } from "@/mocks/news";
import type { Article } from "@/types/news";

const list = articles as Article[];

export default function ArticlePage() {
  const { id } = useParams();
  const article = list.find((item) => item.id === id);

  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [toast, setToast] = useState("");
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      const ratio = total > 0 ? (window.scrollY / total) * 100 : 0;
      setProgress(Math.min(100, Math.max(0, ratio)));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [id]);

  useEffect(() => {
    if (!toast) {
      return;
    }
    const timer = window.setTimeout(() => setToast(""), 2000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const related = useMemo(() => {
    if (!article) {
      return [];
    }
    const sameChannel = list.filter((item) => item.channelSlug === article.channelSlug && item.id !== article.id);
    const others = list.filter((item) => item.channelSlug !== article.channelSlug && item.id !== article.id);
    return [...sameChannel, ...others].slice(0, 3);
  }, [article]);

  const hotSide = useMemo(
    () => [...list].sort((a, b) => b.views - a.views).slice(0, 5),
    []
  );

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-[1280px] flex-1 flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-secondary-500">
            <i className="ri-file-warning-line text-3xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">内容不存在或已下线</h1>
          <p className="mt-3 max-w-md font-body text-sm leading-7 text-foreground-600">
            这条爆料可能已被更正、合并或撤下。你可以返回首页查看今日发布的最新内容。
          </p>
          <Link
            to="/"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
          >
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <PageSeo
        title={`${article.title} | 爆料官网`}
        description={article.summary}
        keywords={`${article.tags.join(",")},爆料官网,今日发布,最新动态`}
        canonicalPath={`/article/${article.id}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: article.title,
            description: article.summary,
            datePublished: "2026-09-10T08:00:00+08:00",
            dateModified: "2026-09-10T08:00:00+08:00",
            inLanguage: "zh-CN",
            articleSection: article.channelName,
            keywords: article.tags.join(","),
            author: { "@type": "Organization", name: article.author },
            publisher: { "@type": "Organization", name: "爆料官网" },
            mainEntityOfPage: `https://byfju.cn/article/${article.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://byfju.cn/" },
              {
                "@type": "ListItem",
                position: 2,
                name: article.channelName,
                item: `https://byfju.cn/channel/${article.channelSlug}`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
                item: `https://byfju.cn/article/${article.id}`,
              },
            ],
          },
        ]}
      />
      <div className="fixed left-0 top-0 z-50 h-0.5 w-full bg-transparent">
        <div className="h-full bg-primary-500 transition-[width] duration-150" style={{ width: `${progress}%` }}></div>
      </div>

      <SiteHeader />
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: article.channelName, to: `/channel/${article.channelSlug}` },
          { label: article.title },
        ]}
      />

      <main className="mx-auto w-full max-w-[1280px] flex-1 px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <article className="lg:col-span-2">
            <header>
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/channel/${article.channelSlug}`}
                  className="rounded bg-primary-100 px-2.5 py-1 font-label text-xs text-primary-700 transition-colors hover:bg-primary-200"
                >
                  {article.channelName}
                </Link>
                {article.hot && (
                  <span className="flex items-center gap-1 rounded bg-accent-100 px-2.5 py-1 font-label text-xs text-accent-800">
                    <i className="ri-fire-fill" aria-hidden="true"></i>
                    热点追踪
                  </span>
                )}
              </div>
              <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[34px]">
                {article.title}
              </h1>
              <p className="mt-4 rounded-md border-l-2 border-primary-400 bg-background-100 px-4 py-3 font-body text-sm leading-7 text-foreground-700">
                {article.summary}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-background-200 pb-4 font-label text-xs text-secondary-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-3-line" aria-hidden="true"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line" aria-hidden="true"></i>
                  {article.publishedAt}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line" aria-hidden="true"></i>
                  阅读 {(article.views / 10000).toFixed(1)} 万
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-timer-line" aria-hidden="true"></i>
                  {article.readTime}
                </span>
              </div>
            </header>

            <figure className="my-6">
              <div className="h-56 w-full overflow-hidden rounded-lg md:h-80">
                <img
                  src={article.cover}
                  alt={`${article.title} 配图`}
                  title={`${article.title} - 爆料官网今日发布`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <figcaption className="mt-3 text-center font-label text-xs text-secondary-500">
                资料图 / 爆料官网编辑部整理
              </figcaption>
            </figure>

            <div className="flex flex-col gap-5">
              {article.content.map((paragraph, index) => (
                <p key={`${article.id}-p-${index}`} className="font-body text-[15px] leading-8 text-foreground-800">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              <span className="font-label text-xs text-secondary-500">相关标签：</span>
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/channel/all?q=${encodeURIComponent(tag)}`}
                  className="rounded-full border border-background-300 px-3 py-1 font-label text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  # {tag}
                </Link>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3 border-y border-background-200 py-5">
              <button
                type="button"
                onClick={() => {
                  setLiked((prev) => !prev);
                  setToast(liked ? "已取消点赞" : "感谢你的认可");
                }}
                className={`flex items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 font-label text-sm transition-colors ${
                  liked
                    ? "border-primary-500 bg-primary-500 text-background-50 dark:text-foreground-950"
                    : "border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
                }`}
              >
                <i className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"} aria-hidden="true"></i>
                {liked ? "已点赞" : "点赞"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSaved((prev) => !prev);
                  setToast(saved ? "已取消收藏" : "已加入收藏");
                }}
                className={`flex items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 font-label text-sm transition-colors ${
                  saved
                    ? "border-accent-500 bg-accent-500 text-background-50"
                    : "border-background-300 text-foreground-700 hover:border-accent-400 hover:text-accent-700"
                }`}
              >
                <i className={saved ? "ri-bookmark-fill" : "ri-bookmark-line"} aria-hidden="true"></i>
                {saved ? "已收藏" : "收藏"}
              </button>
              <Link
                to="/about#submit"
                className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-4 py-2 font-label text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-send-plane-line" aria-hidden="true"></i>
                补充线索
              </Link>
            </div>

            <section className="mt-10" aria-labelledby="related-heading">
              <h2 id="related-heading" className="mb-4 font-heading text-xl font-bold text-foreground-950">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((item) => (
                  <ArticleCard key={item.id} article={item} />
                ))}
              </div>
            </section>
          </article>

          <aside className="flex flex-col gap-6 lg:sticky lg:top-32 lg:self-start">
            <section className="rounded-lg border border-background-200 bg-background-100 p-5" aria-labelledby="article-hot">
              <h2 id="article-hot" className="mb-3 flex items-center gap-2 border-b border-background-200 pb-3 font-heading text-base font-bold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center text-primary-500">
                  <i className="ri-fire-line text-lg" aria-hidden="true"></i>
                </span>
                热门推荐
              </h2>
              <div className="flex flex-col">
                {hotSide.map((item) => (
                  <ArticleCard key={item.id} article={item} variant="compact" />
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">内容说明</h2>
              <ul className="mt-3 flex flex-col gap-3 font-body text-sm leading-6 text-foreground-600">
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" aria-hidden="true"></i>
                  本文信息经两个以上独立信源交叉核对。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" aria-hidden="true"></i>
                  涉及当事方的表述已标注回应状态。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" aria-hidden="true"></i>
                  如需更正，请联系 tips@baoliao-today.example.com。
                </li>
              </ul>
            </section>
          </aside>
        </div>
      </main>

      {toast && (
        <div className="fixed bottom-24 left-1/2 z-50 -translate-x-1/2 rounded-full bg-foreground-950 px-5 py-2.5 font-label text-sm text-background-50 shadow-sm">
          {toast}
        </div>
      )}

      <SiteFooter />
      <BackToTop />
    </div>
  );
}